import type { LayerStepAction, LayerStepState } from "./types";

import { CUSTOM_KEY, LAYER_PRESET_ORDER, SUBLAYER_PRESET_KEYS } from "./constants";

export const initialLayerStepState = (): LayerStepState => ({
  activeLayerId: "",
  activeSubLayerId: "",
  customLayerNameDraft: "",
  editingLayerId: null,
  editingObjectId: null,
  editingSubLayerId: null,
  isDrawing: false,
  isEditing: false,
  newLayerPreset: LAYER_PRESET_ORDER[0],
  newSubLayerKey: SUBLAYER_PRESET_KEYS[LAYER_PRESET_ORDER[0]][0] ?? CUSTOM_KEY,
  selectedObjectId: null,
  subLayerDraftByLayer: {},
});

export function layerStepReducer(state: LayerStepState, action: LayerStepAction): LayerStepState {
  switch (action.type) {
    case "ADD_LAYER_COMMITTED":
      return {
        ...state,
        activeLayerId: action.layerId,
        activeSubLayerId: "",
        customLayerNameDraft: "",
        isDrawing: false,
        isEditing: false,
        selectedObjectId: null,
      };

    case "CLAMP_ACTIVE_LAYER": {
      const { layers } = action;
      if (layers.length === 0) {
        return { ...state, activeLayerId: "", activeSubLayerId: "" };
      }
      if (state.activeLayerId && layers.some((l) => l.id === state.activeLayerId)) {
        return state;
      }
      if (state.activeLayerId && !layers.some((l) => l.id === state.activeLayerId)) {
        const first = layers[0];
        return {
          ...state,
          activeLayerId: first.id,
          activeSubLayerId: first.subLayers[0]?.id ?? "",
        };
      }
      return state;
    }

    case "CLAMP_ACTIVE_SUBLAYER": {
      const { subLayers } = action;
      if (subLayers.length === 0) {
        return { ...state, activeSubLayerId: "" };
      }
      if (state.activeSubLayerId && subLayers.some((s) => s.id === state.activeSubLayerId)) {
        return state;
      }
      if (state.activeSubLayerId && !subLayers.some((s) => s.id === state.activeSubLayerId)) {
        return { ...state, activeSubLayerId: subLayers[0].id };
      }
      return state;
    }

    case "CLEAR_OBJECT_SELECTION":
      return { ...state, editingObjectId: null, selectedObjectId: null };

    case "DELETE_LAYER_UI":
      return { ...state, editingLayerId: null, ...clearSelectionSlice(state) };

    case "DELETE_SUB_LAYER_UI":
      return {
        ...state,
        activeSubLayerId: state.activeSubLayerId === action.subLayerId ? "" : state.activeSubLayerId,
        editingSubLayerId: null,
        ...clearSelectionSlice(state),
      };

    case "END_DRAWING_SESSION":
      return { ...state, isDrawing: false };

    case "ENSURE_NEW_SUB_LAYER_KEY": {
      const sublayerPresetKeys = [...SUBLAYER_PRESET_KEYS[action.preset]];
      if (sublayerPresetKeys.length === 0) {
        return { ...state, newSubLayerKey: CUSTOM_KEY };
      }
      const valid = [...sublayerPresetKeys, CUSTOM_KEY];
      const nextKey = valid.includes(state.newSubLayerKey) ? state.newSubLayerKey : sublayerPresetKeys[0];
      return { ...state, newSubLayerKey: nextKey };
    }

    case "SELECT_OBJECT_ROW":
      return {
        ...state,
        activeLayerId: action.layerId,
        activeSubLayerId: action.subLayerId,
        editingObjectId: null,
        isDrawing: false,
        selectedObjectId: action.objectId,
      };

    case "SELECT_POLYGON":
      return { ...state, editingObjectId: null, selectedObjectId: action.objectId };

    case "SELECT_SUBLAYER_ROW":
      return {
        ...state,
        activeLayerId: action.layerId,
        activeSubLayerId: action.subLayerId,
        editingObjectId: null,
        isDrawing: false,
        selectedObjectId: null,
      };

    case "SET_ACTIVE_LAYER_HEADER":
      return { ...state, activeLayerId: action.layerId };

    case "SET_ACTIVE_SUB_LAYER_ID":
      return { ...state, activeSubLayerId: action.id };

    case "SET_CUSTOM_LAYER_NAME_DRAFT":
      return { ...state, customLayerNameDraft: action.value };

    case "SET_EDITING_LAYER":
      return { ...state, editingLayerId: action.id };

    case "SET_EDITING_OBJECT":
      return { ...state, editingObjectId: action.id };

    case "SET_EDITING_SUBLAYER":
      return { ...state, editingSubLayerId: action.id };

    case "SET_NEW_LAYER_PRESET":
      return { ...state, newLayerPreset: action.preset };

    case "SET_NEW_SUB_LAYER_KEY":
      return { ...state, newSubLayerKey: action.key };

    case "SET_SELECTED_OBJECT":
      return { ...state, selectedObjectId: action.id };

    case "SET_SUB_LAYER_DRAFT":
      return {
        ...state,
        subLayerDraftByLayer: { ...state.subLayerDraftByLayer, [action.layerId]: action.value },
      };

    case "START_DRAWING_SESSION":
      return { ...state, isDrawing: true, isEditing: false, selectedObjectId: null };

    case "START_EDITING_SESSION":
      return { ...state, isDrawing: false, isEditing: true };

    default:
      return state;
  }
}

const clearSelectionSlice = (_state: LayerStepState): Pick<LayerStepState, "editingObjectId" | "selectedObjectId"> => {
  return { editingObjectId: null, selectedObjectId: null };
};
